import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { listTrainingUsers } from "../api/training";
import "./ReportsSectionCompact.css";
import {
  getTrainingProgressSummary,
  getTrainingProgressDetails,
  exportTrainingProgressCsv,
} from "../api/reports";
import { getSalonTimezone } from "../utils/salonTimezone";
import { TRAINING_PROGRESS_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";
import { getReportsHomePathForRoute } from "../utils/reportRouteContext";

const STATUS_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "NOT_STARTED", label: "Not started" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "OVERDUE", label: "Overdue" },
];

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0%";
  return `${Math.round(num)}%`;
};

const statusBadge = (status) => {
  if (status === "Overdue") return "bg-rose-100 text-rose-700 border-rose-200";
  if (status === "Completed") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (status === "In progress") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

export default function TrainingProgressReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSalon, user, token } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.TRAINING_PROGRESS;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -29), salonTimezone);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [roleFilter, setRoleFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sortState, setSortState] = useState({ key: "name", dir: "asc" });
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [staffOptions, setStaffOptions] = useState([]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };

  useEffect(() => {
    if (!token) return;
    let isActive = true;
    listTrainingUsers(token)
      .then((data) => {
        if (!isActive) return;
        setStaffOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isActive) return;
        setStaffOptions([]);
      });
    return () => {
      isActive = false;
    };
  }, [token]);

  const roleOptions = useMemo(() => {
    const roleSet = new Set();
    staffOptions.forEach((entry) => {
      if (entry?.role) roleSet.add(entry.role);
    });
    return Array.from(roleSet).sort();
  }, [staffOptions]);
  const staffLabel = useMemo(() => {
    if (!staffFilter) return "ALL";
    const match = staffOptions.find(
      (entry) => String(entry?.user_id) === String(staffFilter)
    );
    return match?.name || `ID ${staffFilter}`;
  }, [staffFilter, staffOptions]);

  const requestParams = useMemo(
    () => ({
      date_from: startDate,
      date_to: endDate,
      role: roleFilter || undefined,
      staff_id: staffFilter ? Number(staffFilter) : undefined,
      status: statusFilter,
      include_inactive_staff: includeInactive || undefined,
      sort_by: sortState.key,
      sort_dir: sortState.dir,
    }),
    [startDate, endDate, roleFilter, staffFilter, statusFilter, includeInactive, sortState]
  );

  const loadReport = useCallback(async () => {
    if (!user?.salon_id) {
      setError("Select a salon to view reports.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [summaryRes, detailsRes] = await Promise.all([
        getTrainingProgressSummary(requestParams),
        getTrainingProgressDetails(requestParams),
      ]);
      setSummary(summaryRes?.summary || null);
      setRows(Array.isArray(detailsRes?.rows) ? detailsRes.rows : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load training progress.");
      setSummary(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [requestParams, user?.salon_id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const toggleExpanded = (staffId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [staffId]: !prev?.[staffId],
    }));
  };

  const handleSort = (key) => {
    setSortState((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
  };

  const handleExport = async () => {
    try {
      await exportTrainingProgressCsv(requestParams);
    } catch (err) {
      setError(err?.response?.data?.detail || "Export failed.");
    }
  };
  const handleAskAi = () => {
    const roleLabel = roleFilter || "ALL";
    const statusLabel = statusFilter || "ALL";
    const prefillMessage = `Analyze Training Progress for ${startDate} to ${endDate}. Filters: role=${roleLabel}, staff=${staffLabel}, status=${statusLabel}, include_inactive=${includeInactive}. Start with overdue staff and lowest completion.`;
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("sbin:open-ai-assistant", {
        detail: {
          prefillMessage,
          context: {
            intent: "training_progress",
            start_date: startDate,
            end_date: endDate,
          },
        },
      })
    );
  };

  const totals = summary?.totals || {};
  const summaryTiles = [
    {
      label: "Staff with assignments",
      value: totals.staff_with_assignments ?? 0,
      tooltip: TRAINING_PROGRESS_TOOLTIPS.staff_with_assignments,
    },
    {
      label: "In progress",
      value: totals.staff_in_progress ?? 0,
      tooltip: TRAINING_PROGRESS_TOOLTIPS.in_progress,
    },
    {
      label: "Completed all",
      value: totals.staff_completed_all ?? 0,
      tooltip: TRAINING_PROGRESS_TOOLTIPS.completed_all,
    },
    {
      label: "Overdue",
      value: totals.staff_overdue ?? 0,
      tooltip: TRAINING_PROGRESS_TOOLTIPS.overdue,
    },
    {
      label: "Average completion %",
      value: formatPercent(totals.average_completion_pct ?? 0),
      tooltip: TRAINING_PROGRESS_TOOLTIPS.average_completion_pct,
    },
  ];

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Training Progress" onClose={handleClose} />
          <div className="px-6 py-6 space-y-6 text-base md:text-lg">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}

            <div
              id="training-progress-overview"
              className={getSectionClassName("training-progress-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="training-progress-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-4 ${getSectionClassName(
                "training-progress-filters"
              )}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  Date range
                  <InfoTooltip
                    text={TRAINING_PROGRESS_TOOLTIPS.date_range}
                    label="Date range"
                  />
                </div>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <span className="text-sm text-slate-500">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <div className="ml-auto flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAskAi}
                      className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      disabled={loading}
                    >
                      Ask AI
                    </button>
                    <InfoTooltip
                      text="Ask AI questions about this Training Progress report."
                      label="Ask AI"
                    />
                  </div>
                  <button
                    id="training-progress-export"
                    type="button"
                    onClick={handleExport}
                    className={`rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 ${getSectionClassName(
                      "training-progress-export"
                    )}`}
                    disabled={loading}
                  >
                    Export (CSV)
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <label className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    Role
                    <InfoTooltip
                      text={TRAINING_PROGRESS_TOOLTIPS.role}
                      label="Role"
                    />
                  </span>
                  <select
                    value={roleFilter}
                    onChange={(event) => setRoleFilter(event.target.value)}
                    className="rounded border border-slate-300 px-3 py-2 text-sm bg-white"
                  >
                    <option value="">All roles</option>
                    {roleOptions.map((roleValue) => (
                      <option key={roleValue} value={roleValue}>
                        {roleValue}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    Staff
                    <InfoTooltip
                      text={TRAINING_PROGRESS_TOOLTIPS.staff}
                      label="Staff"
                    />
                  </span>
                  <select
                    value={staffFilter}
                    onChange={(event) => setStaffFilter(event.target.value)}
                    className="rounded border border-slate-300 px-3 py-2 text-sm bg-white"
                  >
                    <option value="">All staff</option>
                    {staffOptions.map((staff) => (
                      <option key={staff.user_id} value={staff.user_id}>
                        {staff.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    Status
                    <InfoTooltip
                      text={TRAINING_PROGRESS_TOOLTIPS.status}
                      label="Status"
                    />
                  </span>
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="rounded border border-slate-300 px-3 py-2 text-sm bg-white"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(event) => setIncludeInactive(event.target.checked)}
                  />
                  <span className="flex items-center gap-2">
                    Include inactive staff
                    <InfoTooltip
                      text={TRAINING_PROGRESS_TOOLTIPS.include_inactive_staff}
                      label="Include inactive staff"
                    />
                  </span>
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                      statusFilter === option.value
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div
              id="training-progress-kpis"
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 ${getSectionClassName(
                "training-progress-kpis"
              )}`}
            >
              {summaryTiles.map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-2xl border border-slate-200 bg-white p-4"
                >
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    {tile.label}
                    {tile.tooltip ? (
                      <InfoTooltip text={tile.tooltip} label={tile.label} />
                    ) : null}
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {tile.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              id="training-progress-details"
              className={`rounded-2xl border border-slate-200 bg-white overflow-x-auto ${getSectionClassName(
                "training-progress-details"
              )}`}
            >
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <tr>
                    <SortableTh
                      label="Staff"
                      sortKey="name"
                      sortState={sortState}
                      onSort={handleSort}
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.column_staff}
                      className="px-3 py-3"
                    />
                    <SortableTh
                      label="Role"
                      sortKey="role"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.column_role}
                    />
                    <SortableTh
                      label="Assigned"
                      sortKey="assigned_courses"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3 text-right"
                      align="right"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.column_assigned}
                    />
                    <SortableTh
                      label="Completed"
                      sortKey="completed_courses"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3 text-right"
                      align="right"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.column_completed}
                    />
                    <SortableTh
                      label="Completion %"
                      sortKey="completion_pct"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3 text-right"
                      align="right"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.completion_pct}
                    />
                    <SortableTh
                      label="Overdue"
                      sortKey="overdue_count"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3 text-right"
                      align="right"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.overdue}
                    />
                    <SortableTh
                      label="Last activity"
                      sortKey="last_activity"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.last_activity}
                    />
                    <SortableTh
                      label="Next due"
                      sortKey="next_due"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.next_due}
                    />
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      sortState={sortState}
                      onSort={handleSort}
                      className="px-3 py-3"
                      tooltip={TRAINING_PROGRESS_TOOLTIPS.column_status}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading && (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-slate-500">
                        Loading...
                      </td>
                    </tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-slate-500">
                        No training progress found for this range.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    rows.map((row) => (
                      <Fragment key={row.staff_id}>
                        <tr
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleExpanded(row.staff_id)}
                        >
                          <td className="px-3 py-3 font-semibold text-slate-900 whitespace-nowrap">
                            {row.staff_name}
                          </td>
                          <td className="px-3 py-3 text-slate-700">
                            {row.role}
                          </td>
                          <td className="px-3 py-3 text-right text-slate-700">
                            {row.assigned_courses}
                          </td>
                          <td className="px-3 py-3 text-right text-slate-700">
                            {row.completed_courses}
                          </td>
                          <td className="px-3 py-3 text-right text-slate-700">
                            {formatPercent(row.completion_pct)}
                          </td>
                          <td className="px-3 py-3 text-right text-slate-700">
                            {row.overdue_count}
                          </td>
                          <td className="px-3 py-3 text-slate-700">
                            {row.last_activity || "-"}
                          </td>
                          <td className="px-3 py-3 text-slate-700">
                            {row.next_due || "-"}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusBadge(
                                row.status
                              )}`}
                            >
                              {row.status}
                            </span>
                          </td>
                        </tr>
                        {expandedRows?.[row.staff_id] && (
                          <tr key={`${row.staff_id}-details`}>
                            <td colSpan={9} className="px-3 py-4 bg-slate-50">
                              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <div className="text-sm font-semibold text-slate-900 mb-3">
                                  Course details
                                </div>
                                {row.courses?.length ? (
                                  <table className="min-w-full text-xs">
                                    <thead className="text-slate-500 uppercase tracking-wide">
                                      <tr>
                                        <th className="text-left px-2 py-2">Course</th>
                                        <th className="text-left px-2 py-2">Assigned</th>
                                        <th className="text-left px-2 py-2">Due</th>
                                        <th className="text-right px-2 py-2">Completion %</th>
                                        <th className="text-right px-2 py-2">Modules</th>
                                        <th className="text-right px-2 py-2">Lessons</th>
                                        <th className="text-right px-2 py-2">Assignments</th>
                                        <th className="text-left px-2 py-2">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {row.courses.map((course) => (
                                        <tr key={`${row.staff_id}-${course.course_id}`}>
                                          <td className="px-2 py-2 text-slate-700">
                                            {course.course_name}
                                          </td>
                                          <td className="px-2 py-2 text-slate-600">
                                            {course.assigned_on || "-"}
                                          </td>
                                          <td className="px-2 py-2 text-slate-600">
                                            {course.due_on || "-"}
                                          </td>
                                          <td className="px-2 py-2 text-right text-slate-700">
                                            {formatPercent(course.completion_pct)}
                                          </td>
                                          <td className="px-2 py-2 text-right text-slate-600">
                                            {course.modules_completed}/{course.modules_total}
                                          </td>
                                          <td className="px-2 py-2 text-right text-slate-600">
                                            {course.lessons_completed}/{course.lessons_total}
                                          </td>
                                          <td className="px-2 py-2 text-right text-slate-600">
                                            {course.assignments_passed}/{course.assignments_total}
                                          </td>
                                          <td className="px-2 py-2 text-slate-700">
                                            {course.status}
                                            {course.overdue_reason
                                              ? ` - ${course.overdue_reason}`
                                              : ""}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div className="text-sm text-slate-500">
                                    No courses assigned.
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

